import React from 'react';
import {Captcha} from 'recaptz';
import {Container, Form, Button, Alert, Spinner} from 'react-bootstrap';
import axios from 'axios';
import {useNavigate} from 'react-router-dom';
import {getAxiosErrorMessage} from '../libs/helper';
import {useAuth} from '../auth/auth-context';

/**
 * RequestPasswordResetPage
 *
 * Calls POST /api/request_password_reset?email=... to request a password reset.
 * If the user is authenticated and has an email, the email field is prefilled and read-only.
 */
export default function RequestPasswordResetPage(): React.JSX.Element {
  const {email: authEmail, isAuthenticated} = useAuth();
  const navigate = useNavigate();

  const [captchaSolved, setCaptchaSolved] = React.useState<boolean>(false);
  const [email, setEmail] = React.useState<string>(authEmail ?? '');
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [success, setSuccess] = React.useState<boolean>(false);

  React.useEffect(() => {
    // Keep local email in sync if auth changes
    setEmail(authEmail ?? '');
  }, [authEmail]);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(undefined);

    const trimmed = email.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      await axios.post('/api/request_password_reset', null, {
        params: {email: trimmed},
      });
      setSuccess(true);
    } catch (error: any) {
      const message =
        getAxiosErrorMessage(error) ?? 'Failed to request password reset';
      setError(String(message));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Container className='my-5'>
      <h1>Request Password Reset</h1>
      {!captchaSolved && (
        <Captcha
          type='mixed'
          length={8}
          onValidate={setCaptchaSolved}
          showSuccessAnimation
        />
      )}

      {captchaSolved && (
        <>
          {error && <Alert variant='danger'>{error}</Alert>}

          {success ? (
            <Alert variant='success'>
              If an account with that email exists, a password reset email has
              been sent. Please check your email (and spam folder) for
              instructions.
              <div className='mt-3'>
                <Button
                  variant='outline-primary'
                  onClick={() => {
                    void navigate('/login');
                  }}
                >
                  Back to sign in
                </Button>
              </div>
            </Alert>
          ) : (
            <Form
              onSubmit={event => {
                void handleSubmit(event);
              }}
            >
              <Form.Group controlId='request-reset-email' className='mb-3'>
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type='email'
                  value={email}
                  onChange={event => {
                    setEmail(event.target.value);
                  }}
                  placeholder='daisy@example.com'
                  readOnly={Boolean(isAuthenticated && authEmail)}
                  required
                />
                {isAuthenticated && !authEmail && (
                  <Form.Text className='text-muted'>
                    No email available for the signed-in user.
                  </Form.Text>
                )}
              </Form.Group>

              <Button type='submit' disabled={loading}>
                {loading ? (
                  <>
                    <Spinner animation='border' size='sm' className='me-2' />{' '}
                    Sending...
                  </>
                ) : (
                  'Send password reset email'
                )}
              </Button>
            </Form>
          )}
        </>
      )}
    </Container>
  );
}
